import { ISaveManager, SavedData, SaveManagerId } from './ISaveManager';
import { logger } from '../Analytics/Logger';
import { LocalSaveManager } from './LocalSaveManager';
import { CloudSaveManager } from './CloudSaveManager';
import { getCurrentUser } from '../api/authManager';
import { DeviceIdManager } from './deviceIdManager';

/**
 * Constants for player identification
 */
const ANONYMOUS_PLAYER_ID = 'anonymous';

/**
 * Reconciliation strategy for when multiple save managers have different data
 */
export enum ReconciliationStrategy {
  Newest = 'newest',
  HighestPriority = 'highest-priority',
  Manual = 'manual',
}

/**
 * Options for SaveDataOrchestrator
 */
export interface OrchestratorOptions {
  /**
   * How to resolve conflicts when multiple save managers have different data
   * - 'newest': Use the data with the most recent timestamp
   * - 'highest-priority': Use data from the save manager with highest priority
   * - 'manual': Call onConflict callback to resolve manually
   */
  reconciliationStrategy?: ReconciliationStrategy;

  /**
   * Callback for manual conflict resolution
   */
  onConflict?: (saves: SavedData[]) => SavedData;

  /**
   * Whether to automatically sync to all enabled save managers on load
   * If true, when loading, will save the reconciled data to all enabled managers
   */
  autoSync?: boolean;
}

/**
 * SaveDataOrchestrator - coordinates multiple save managers
 * Handles save/load operations across local, cloud, and any other backends
 * Automatically reconciles conflicts between different save sources
 */
export class SaveDataOrchestrator<TGameData = any> {
  private saveManagers: ISaveManager<TGameData>[];
  private options: Required<OrchestratorOptions>;

  constructor(
    saveManagers: ISaveManager<TGameData>[],
    options: OrchestratorOptions = {}
  ) {
    this.saveManagers = saveManagers;
    this.options = {
      reconciliationStrategy: options.reconciliationStrategy || ReconciliationStrategy.Newest,
      onConflict: options.onConflict || (() => { throw new Error('Manual reconciliation required but no callback provided'); }),
      autoSync: options.autoSync || false,
    };
  }

  /**
   * Reconcile multiple saves using device-aware strategy
   * 
   * Strategy: Always prefer cloud save UNLESS:
   * - Cloud save is older than local save AND
   * - Cloud save was created by this device (offline play scenario)
   * 
   * If no cloud save exists, picks the newest save.
   */
  private reconcileSaves(validSaves: SavedData<TGameData>[]): SavedData<TGameData> {
    const currentDeviceId = DeviceIdManager.getDeviceId();
    const cloudSave = validSaves.find(s => s.source === SaveManagerId.Cloud);
    const localSave = validSaves.find(s => s.source === SaveManagerId.Local);

    // If both cloud and local exist, apply device-aware reconciliation
    if (cloudSave && localSave) {
      const cloudIsOlder = cloudSave.timestamp < localSave.timestamp;
      const cloudIsFromThisDevice = cloudSave.deviceId === currentDeviceId;

      if (cloudIsOlder && cloudIsFromThisDevice) {
        // Exception: Cloud is older AND from this device - use local (offline play scenario)
        logger.debug(`[SaveDataOrchestrator] Using local save (cloud is older and from this device)`);
        return localSave;
      } else {
        // Default: Always prefer cloud
        logger.debug(`[SaveDataOrchestrator] Using cloud save, as per default (cloud timestamp: ${cloudSave.timestamp}, local timestamp: ${localSave.timestamp})`);
        return cloudSave;
      }
    }

    // No cloud save, or missing one of the saves - pick newest with cloud preference on ties
    const reconciledSave = validSaves.reduce((best, current) => {
      if (current.timestamp > best.timestamp) {
        return current;
      } else if (current.timestamp === best.timestamp) {
        // Same timestamp - prefer cloud data as source of truth
        return current.source === SaveManagerId.Cloud ? current : best;
      }
      return best;
    });

    return reconciledSave;
  }

  /**
   * Save game data to specified save managers (or all enabled managers if not specified)
   */
  async save(
    gameId: string,
    playerId: string,
    data: TGameData,
    timestamp: number = Date.now(),
    managers?: ISaveManager<TGameData>[],
    expirationDate: Date = new Date(3000, 1, 1)
  ): Promise<void> {
    const targetManagers = managers || this.saveManagers.filter(m => m.isEnabled());

    if (targetManagers.length === 0) {
      logger.warn('[SaveDataOrchestrator] No save managers to save to');
      return;
    }

    const deviceId = DeviceIdManager.getDeviceId();

    // Save to all target managers in parallel
    const savePromises = targetManagers.map(manager => 
      manager.save(gameId, playerId, data, timestamp, deviceId, expirationDate)
        .catch(error => {
          logger.error(`[SaveDataOrchestrator] Failed to save via ${manager.id}:`, error);
          // Don't throw - allow other saves to succeed
        })
    );

    await Promise.all(savePromises);
    logger.debug(`[SaveDataOrchestrator] Queued up saves for "${gameId}" to ${targetManagers.length} manager(s)`);
  }

  /**
   * Load game data from all enabled save managers and reconcile
   * Returns SavedData object containing both data and timestamp
   */
  async load(gameId: string, playerId: string): Promise<SavedData<TGameData> | null> {
    logger.debug(`[SaveDataOrchestrator] Loading all game data for game "${gameId}" for player "${playerId}"...`);
    const enabledManagers = this.saveManagers.filter(m => m.isEnabled());

    if (enabledManagers.length === 0) {
      logger.warn('[SaveDataOrchestrator] No enabled save managers');
      return null;
    }

    // Load from all enabled managers in parallel
    const loadPromises = enabledManagers.map(manager =>
      manager.load(gameId, playerId)
        .catch(error => {
          logger.error(`[SaveDataOrchestrator] Failed to load via ${manager.id}:`, error);
          return null;
        })
    );

    const results = await Promise.all(loadPromises);
    const validSaves = results.filter((save): save is SavedData<TGameData> => save !== null);

    if (validSaves.length === 0) {
      logger.debug('[SaveDataOrchestrator] No saved data found');
      return null;
    }

    if (validSaves.length === 1) {
      const singleSave = validSaves[0];
      logger.debug(`[SaveDataOrchestrator] Loaded ${gameId} from ${singleSave.source}`);
      
      // Sync to other enabled managers that don't have data
      const managersWithoutData = enabledManagers.filter(manager => 
        !validSaves.some(save => save.source === manager.id)
      );
      
      if (managersWithoutData.length > 0) {
        logger.info(`[SaveDataOrchestrator] saving data from ${singleSave.source} to ${managersWithoutData.length} manager(s): ${managersWithoutData.map(m => m.id).join(', ')}`);
        // Fire and forget - don't await
        this.save(gameId, playerId, singleSave.savedGameData, singleSave.timestamp, managersWithoutData);
      }
      
      return singleSave;
    }

    // Multiple saves found - reconcile
    const reconciledSave = this.reconcileSaves(validSaves);
    
    logger.debug(
      `[SaveDataOrchestrator] Reconciled ${gameId} from ${validSaves.length} source(s), using ${reconciledSave.source} (timestamp: ${reconciledSave.timestamp}). ` +
      `Other sources: ${validSaves.filter(s => s !== reconciledSave).map(s => `${s.source}:${s.timestamp}`).join(', ')}`
    );

    // Sync the reconciled data to any out-of-date managers
    const outOfDateManagers = enabledManagers.filter(manager => {
      const managerSave = validSaves.find(save => save.source === manager.id);
      return managerSave?.source !== reconciledSave.source && managerSave?.timestamp !== reconciledSave.timestamp;
    });

    if (outOfDateManagers.length > 0) {
      logger.info(`[SaveDataOrchestrator] Saving reconciled data to ${outOfDateManagers.length} out-of-date manager(s): ${outOfDateManagers.map(m => m.id).join(', ')}`);
      // Fire and forget - don't await
      this.save(gameId, playerId, reconciledSave.savedGameData, reconciledSave.timestamp, outOfDateManagers);
    }
    
    return reconciledSave;
  }

  /**
   * Shared orchestrator instance - created once and reused
   */
  private static orchestratorInstance: SaveDataOrchestrator<any> | null = null;

  /**
   * Get or create the shared save orchestrator
   * Generic type parameter is for type-safety at call site; internally uses SaveDataOrchestrator<any>
   */
  private static getSharedOrchestrator<T = any>(): SaveDataOrchestrator<T> {
    if (!SaveDataOrchestrator.orchestratorInstance) {
      const localManager = new LocalSaveManager();
      const cloudManager = new CloudSaveManager();
      
      SaveDataOrchestrator.orchestratorInstance = new SaveDataOrchestrator(
        [localManager, cloudManager],
        {
          reconciliationStrategy: ReconciliationStrategy.Newest,
          autoSync: false, // False until we can implement it
        }
      );
    }
    
    return SaveDataOrchestrator.orchestratorInstance as SaveDataOrchestrator<T>;
  }

  /**
   * Save game data to all enabled save managers (local + cloud if authenticated)
   * 
   * @param gameId The game identifier (e.g., 'tile-clicker', 'word-drift')
   * @param data The game data to save (can be any JSON-serializable object)
   * @param expirationDate Optional expiration date for local storage cleanup (defaults to year 3000 - effectively forever)
   * @param localOnly Optional flag to save only to local storage (skips cloud)
   * 
   * @example
   * await SaveDataOrchestrator.saveGameData('tile-clicker', {
   *   levelScores: { level1: { isWon: true, bestTime: 30 } },
   *   hasCompletedTutorial: true
   * });
   * 
   * @example Save temporary data with expiration
   * await SaveDataOrchestrator.saveGameData(
   *   'analytics',
   *   eventData,
   *   new Date(Date.now() + 24 * 60 * 60 * 1000) // 1 day
   * );
   */
  static async saveGameData(
    gameId: string,
    data: any,
    expirationDate: Date = new Date(3000, 1, 1),
    localOnly: boolean = false
  ): Promise<void> {
    const user = getCurrentUser();
    const playerId = user?.playerId || ANONYMOUS_PLAYER_ID;
    const saveTimestamp = Date.now();
    const deviceId = DeviceIdManager.getDeviceId();
    
    if (localOnly) {
      // Only save to local storage
      const localManager = new LocalSaveManager();
      await localManager.save(gameId, playerId, data, saveTimestamp, deviceId, expirationDate);
    } else {
      // Use full orchestration (local + cloud if available)
      const orchestrator = SaveDataOrchestrator.getSharedOrchestrator();
      await orchestrator.save(gameId, playerId, data, saveTimestamp, undefined, expirationDate);
    }
  }

  /**
   * Load game data from all enabled save managers and reconcile
   * Returns SavedData object with data and timestamp metadata
   * 
   * @param gameId The game identifier (e.g., 'tile-clicker', 'word-drift')
   * @param localOnly Optional flag to load only from local storage (skips cloud)
   * @returns SavedData object containing data and timestamp, or null if no save exists
   * 
   * @example
   * ```typescript
   * import { TileClickerSaveData } from './types/SaveData';
   * 
   * const result = await SaveDataOrchestrator.loadGameData<TileClickerSaveData>('tile-clicker');
   * if (result) {
   *   console.log('Loaded level scores:', result.savedGameData.levelScores); // Type-safe!
   *   console.log('Last saved:', result.timestamp);
   * }
   * ```
   */
  static async loadGameData<TData = any>(gameId: string, localOnly: boolean = false): Promise<SavedData<TData> | null> {
    const user = getCurrentUser();
    const playerId = user?.playerId || ANONYMOUS_PLAYER_ID;
    
    if (localOnly) {
      // Only load from local storage
      const localManager = new LocalSaveManager<TData>();
      return await localManager.load(gameId, playerId);
    } else {
      // Use full orchestration (load and reconcile)
      const orchestrator = SaveDataOrchestrator.getSharedOrchestrator<TData>();
      return await orchestrator.load(gameId, playerId);
    }
  }

}
